import React, { useState, useEffect, useCallback } from 'react';
import { paymentService } from '../../services/payments';
import { authService } from '../../services/auth';
import Alert from '../../components/Common/Alert';
import LoadingSpinner from '../../components/Common/LoadingSpinner';

const creditPackages = [
  // Regular packages
  { Plan_Name: 'Starter', credits: 7, price: 99, original_price: 99, dis: '0%', popular: false, tag: 'Entry Pack' },
  { Plan_Name: 'Value', credits: 21, price: 199, original_price: 297, dis: '33%', popular: false, tag: 'Value Pack' },
  { Plan_Name: 'Saver', credits: 45, price: 399, original_price: 636, dis: '48%', popular: true, tag: 'Popular' },
  { Plan_Name: 'Pro', credits: 78, price: 699, original_price: 1103, dis: '37%', popular: false, tag: 'Pro Pack' },
  { Plan_Name: 'Mega', credits: 150, price: 999, original_price: 2121, dis: '53%', popular: false, tag: 'Best value' }
];

const firstPurchaseOffer = [
  // Only show these packages for the first purchase
  { Plan_Name: 'Starter', credits: 13, price: 99, original_price: 99, dis: '0%', popular: false, tag: 'First Purchase' },
  { Plan_Name: 'Value', credits: 54, price: 299, original_price: 636, dis: '53%', popular: false, tag: 'First Purchase' },
  { Plan_Name: 'Saver', credits: 110, price: 399, original_price: 636, dis: '53%', popular: false, tag: 'First Purchase' },
  { Plan_Name: 'Pro', credits: 200, price: 699, original_price: 1103, dis: '37%', popular: false, tag: 'First Purchase' },
  { Plan_Name: 'Mega', credits: 400, price: 999, original_price: 2121, dis: '53%', popular: false, tag: 'First Purchase' }
];

const CreditsManagement = ({ refreshUser }) => {
  const [credits, setCredits] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [alert, setAlert] = useState(null);
  const [creditPackage, setCreditPackage] = useState(1);
  const [isFirstPurchase, setIsFirstPurchase] = useState(true);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  // Usage history states
  const [showUsageHistory, setShowUsageHistory] = useState(false);
  const [, setUsageHistory] = useState([]);
  const [loadingUsage, setLoadingUsage] = useState(false);

  const filterTransactions = useCallback(() => {
    let filtered = transactions;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(transaction =>
        transaction.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction.razorpay_payment_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction.id?.toString().includes(searchTerm)
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(transaction => transaction.status === filterStatus);
    }

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(transaction => transaction.transaction_type === filterType);
    }

    // Date range filter
    if (dateRange.start) {
      filtered = filtered.filter(transaction =>
        new Date(transaction.created_at) >= new Date(dateRange.start)
      );
    }
    if (dateRange.end) {
      filtered = filtered.filter(transaction =>
        new Date(transaction.created_at) <= new Date(dateRange.end + 'T23:59:59')
      );
    }

    setFilteredTransactions(filtered);
  }, [searchTerm, filterStatus, filterType, dateRange, transactions]);

  const fetchCreditsData = useCallback(async () => {
    try {
      setLoading(true);
      const [creditsData, transactionsData] = await Promise.all([
        paymentService.getSellerCredits(),
        paymentService.getCreditTransactions()
      ]);
      setCredits(creditsData.credits);
      const transactionsList = transactionsData.transactions || [];
      setTransactions(transactionsList);
      setFilteredTransactions(transactionsList);

      // Check if user has made any previous purchases
      const hasPreviousPurchases = transactionsList.some(
        transaction => transaction.transaction_type === 'purchase' && transaction.status === 'completed'
      );
      setIsFirstPurchase(!hasPreviousPurchases);

      // Set default package based on purchase history
      if (!hasPreviousPurchases && firstPurchaseOffer.length > 0) {
        setCreditPackage(firstPurchaseOffer[0].credits);
      } else {
        setCreditPackage(creditPackages[0].credits);
      }
    } catch (error) {
      console.error('Error fetching credits data:', error);
      setAlert({
        type: 'danger',
        message: 'Failed to load credits data. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCreditsData();
  }, [fetchCreditsData]);

  // Search and filter functions
  useEffect(() => {
    filterTransactions();
  }, [filterTransactions]);

  const fetchUsageHistory = async () => {
    try {
      setLoadingUsage(true);
      // This would be a new API endpoint for usage history
      const response = await paymentService.getUsageHistory();
      setUsageHistory(response.usage_history || []);
    } catch (error) {
      console.error('Error fetching usage history:', error);
      setAlert({
        type: 'danger',
        message: 'Failed to load usage history. Please try again.'
      });
    } finally {
      setLoadingUsage(false);
    }
  };

  const downloadInvoice = async (transaction) => {
    try {
      if (transaction.transaction_type !== 'purchase' || transaction.status !== 'completed') {
        setAlert({
          type: 'warning',
          message: 'Invoice is only available for completed purchases.'
        });
        return;
      }

      const response = await paymentService.downloadInvoice(transaction.id);

      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${transaction.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      setAlert({
        type: 'danger',
        message: 'Failed to download invoice. Please try again.'
      });
    }
  };


  const exportToCSV = () => {
    if (filteredTransactions.length === 0) {
      setAlert({
        type: 'warning',
        message: 'No transactions to export.'
      });
      return;
    }

    const headers = ['Date', 'Type', 'Credits', 'Amount', 'Status', 'Description', 'Payment ID'];
    const csvContent = [
      headers.join(','),
      ...filteredTransactions.map(transaction => [
        formatDate(transaction.created_at),
        transaction.transaction_type === 'purchase' ? 'Purchase' : 'Usage',
        transaction.credits,
        transaction.amount || '',
        transaction.status,
        `"${transaction.description || ''}"`,
        transaction.razorpay_payment_id || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `credit-transactions-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const downloadReport = async () => {
    try {
      const response = await paymentService.downloadReport({
        start_date: dateRange.start,
        end_date: dateRange.end,
        status: filterStatus,
        type: filterType
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `credit-report-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading report:', error);
      setAlert({
        type: 'danger',
        message: 'Failed to download report. Please try again.'
      });
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setFilterType('all');
    setDateRange({ start: '', end: '' });
  };

  const handlePurchaseCredits = async () => {
    try {
      setPurchasing(true);

      // Find selected package from both regular and first purchase offers
      let selectedPackage = creditPackages.find(pkg => pkg.credits === creditPackage);
      if (!selectedPackage && isFirstPurchase) {
        selectedPackage = firstPurchaseOffer.find(pkg => pkg.credits === creditPackage);
      }

      if (!selectedPackage) {
        setAlert({
          type: 'danger',
          message: 'Please select a valid credit package.'
        });
        return;
      }

      const response = await paymentService.purchaseCredits({
        credits: selectedPackage.credits,
        amount: selectedPackage.price
      });

      console.log('Purchase credits response:', response);
      console.log('Selected package:', selectedPackage);

      if (response.razorpay_order_id) {
        // Check if Razorpay is loaded
        if (typeof window.Razorpay === 'undefined') {
          setAlert({
            type: 'danger',
            message: 'Payment system is loading. Please try again in a moment.'
          });
          return;
        }

        // Get current user for prefill
        const currentUser = await authService.getCurrentUser();

        // Initialize Razorpay payment
        const options = {
          key: response.key,
          amount: response.amount * 100,
          currency: response.currency,
          order_id: response.razorpay_order_id,
          name: 'WhatNew Credits',
          description: `Purchase ${selectedPackage.credits} Credits`,
          image: '/logo192.png',
          prefill: {
            name: currentUser?.user?.get_full_name || currentUser?.user?.username || '',
            email: currentUser?.user?.email || '',
            contact: currentUser?.user?.phone_number || ''
          },
          theme: {
            color: '#007bff'
          },
          handler: async function (razorpayResponse) {
            console.log('Razorpay payment successful:', razorpayResponse);
            try {
              // Verify payment with backend
              const verifyResponse = await paymentService.verifyPayment({
                razorpay_order_id: razorpayResponse.razorpay_order_id,
                razorpay_payment_id: razorpayResponse.razorpay_payment_id,
                razorpay_signature: razorpayResponse.razorpay_signature
              });

              console.log('Payment verification response:', verifyResponse);

              setAlert({
                type: 'success',
                message: verifyResponse.message || `Successfully purchased ${selectedPackage.credits} credits!`
              });

              // Refresh data
              fetchCreditsData();

              // Refresh user data to update navbar
              if (refreshUser) {
                refreshUser();
              }
            } catch (verifyError) {
              console.error('Payment verification failed:', verifyError);
              setAlert({
                type: 'danger',
                message: 'Payment completed but verification failed. Please contact support if credits are not added.'
              });
            }
          },
          modal: {
            ondismiss: async function () {
              console.log('Razorpay payment modal dismissed');
              try {
                // Handle payment failure/cancellation
                await paymentService.handlePaymentFailure({
                  razorpay_order_id: response.razorpay_order_id,
                  error_reason: 'Payment cancelled by user'
                });
              } catch (error) {
                console.error('Error handling payment cancellation:', error);
              }
            }
          }
        };

        const razorpay = new window.Razorpay(options);

        razorpay.on('payment.failed', async function (response) {
          console.error('Razorpay payment failed:', response.error);
          try {
            // Handle payment failure
            await paymentService.handlePaymentFailure({
              razorpay_order_id: response.error.metadata?.order_id,
              razorpay_payment_id: response.error.metadata?.payment_id,
              error_reason: response.error.description
            });
          } catch (error) {
            console.error('Error handling payment failure:', error);
          }

          setAlert({
            type: 'danger',
            message: `Payment failed: ${response.error.description}`
          });
        });

        razorpay.open();
      } else {
        setAlert({
          type: 'success',
          message: `Successfully purchased ${selectedPackage.credits} credits!`
        });
        fetchCreditsData();

        // Refresh user data to update navbar
        if (refreshUser) {
          refreshUser();
        }
      }
    } catch (error) {
      console.error('Error purchasing credits:', error);
      console.error('Error response data:', error.response?.data);
      console.error('Error status:', error.response?.status);
      setAlert({
        type: 'danger',
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to purchase credits. Please try again.'
      });
    } finally {
      setPurchasing(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#0a0f1e' }}>
      <div className="max-w-7xl mx-auto">
          {/* Enhanced Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div>
              <h2 className="text-3xl font-bold text-white tracking-tight mb-1">
                <i className="bi bi-coin me-2" style={{ color: '#ef4444' }}></i>
                Credits Management
              </h2>
              <p className="text-xs uppercase font-bold tracking-wider" style={{ color: '#64748b' }}>
                Manage your livestream credits and track usage
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3 px-6 py-3 rounded-2xl shadow-lg" style={{ backgroundColor: '#161e35', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                <i className="bi bi-wallet2 text-2xl" style={{ color: '#ef4444' }}></i>
                <div>
                  <span className="text-white font-bold text-xl block leading-none">{credits} Credits</span>
                  <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#64748b' }}>Available Balance</span>
                </div>
              </div>
            </div>
          </div>

          {alert && (
            <Alert
              type={alert.type}
              message={alert.message}
              onClose={() => setAlert(null)}
              className="mb-4"
            />
          )}

          {/* Enhanced Credits Info */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-2 rounded-2xl p-8" style={{ backgroundColor: '#161e35' }}>
              <div className="flex items-center gap-3 mb-8">
                <i className="bi bi-info-circle text-xl" style={{ color: '#ef4444' }}></i>
                <h5 className="text-lg font-bold text-white mb-0">How Credits Work</h5>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
                    <i className="bi bi-check-circle text-xl" style={{ color: '#10b981' }}></i>
                  </div>
                  <div>
                    <h6 className="text-white font-bold mb-1">Free Credit on Registration</h6>
                    <p className="text-sm" style={{ color: '#94a3b8' }}>Get 1 free credit when you become a verified seller</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                    <i className="bi bi-clock text-xl" style={{ color: '#3b82f6' }}></i>
                  </div>
                  <div>
                    <h6 className="text-white font-bold mb-1">Usage Rate</h6>
                    <p className="text-sm" style={{ color: '#94a3b8' }}>1 credit per 30 minutes of high-definition livestreaming</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)' }}>
                    <i className="bi bi-infinity text-xl" style={{ color: '#f59e0b' }}></i>
                  </div>
                  <div>
                    <h6 className="text-white font-bold mb-1">Never Expire</h6>
                    <p className="text-sm" style={{ color: '#94a3b8' }}>Purchased credits remain valid indefinitely in your wallet</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                    <i className="bi bi-lightning text-xl" style={{ color: '#ef4444' }}></i>
                  </div>
                  <div>
                    <h6 className="text-white font-bold mb-1">Instant Activation</h6>
                    <p className="text-sm" style={{ color: '#94a3b8' }}>Use credits immediately after successful purchase</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl p-8 flex flex-col items-center justify-center text-center relative overflow-hidden" style={{ backgroundColor: '#161e35' }}>
              <div className="absolute inset-0 flex items-center justify-center opacity-[0.03]">
                <i className="bi bi-wallet2" style={{ fontSize: '180px', color: '#ef4444' }}></i>
              </div>
              <div className="relative z-10 w-full">
                <p className="text-xs uppercase font-bold tracking-wider mb-2" style={{ color: '#64748b' }}>Current Balance</p>
                <div className="text-7xl font-bold mb-2" style={{ color: '#ef4444' }}>{credits}</div>
                <p className="text-sm mb-6" style={{ color: '#94a3b8' }}>Credits Available</p>
                
                {credits === 0 ? (
                  <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(239, 68, 68, 0.05)', border: '1px solid rgba(239, 68, 68, 0.1)' }}>
                    <p className="text-sm font-bold text-danger mb-1">
                      <i className="bi bi-exclamation-triangle-fill mr-2"></i>
                      No credits left!
                    </p>
                    <p className="text-xs" style={{ color: '#64748b' }}>Purchase credits to start livestreaming</p>
                  </div>
                ) : (
                  <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.1)' }}>
                    <p className="text-sm font-bold text-success mb-1">
                      <i className="bi bi-check-circle-fill mr-2"></i>
                      Ready to stream!
                    </p>
                    <p className="text-xs" style={{ color: '#64748b' }}>~{Math.floor(credits * 0.5)} hours available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Enhanced Purchase Credits */}
          <div className="rounded-2xl p-8 mb-8" style={{ backgroundColor: '#161e35' }}>
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <i className="bi bi-cart-plus text-xl" style={{ color: '#ef4444' }}></i>
                <h5 className="text-lg text-white mb-0">Purchase Credits</h5>
              </div>
              {isFirstPurchase && (
                <span className="px-5 py-2 rounded-xl text-xs font-bold uppercase tracking-wider" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                  <i className="bi bi-gift-fill mr-2"></i>
                  First Purchase Special
                </span>
              )}
            </div>

            {/* Special Offers Section */}
            {isFirstPurchase && firstPurchaseOffer.length > 0 && (
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#ef4444' }}></div>
                  <h6 className="text-white font-bold uppercase tracking-widest text-[10px] mb-0">Exclusive First Purchase Rewards</h6>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                  {firstPurchaseOffer.map((pkg) => (
                    <div
                      key={`first-${pkg.credits}`}
                      className="rounded-2xl p-7 cursor-pointer transition-all relative overflow-hidden flex flex-col group border-2"
                      style={{
                        backgroundColor: '#0f1420',
                        borderColor: creditPackage === pkg.credits ? '#ef4444' : 'rgba(255, 255, 255, 0.05)',
                        boxShadow: creditPackage === pkg.credits ? '0 20px 40px rgba(239, 68, 68, 0.15)' : 'none'
                      }}
                      onClick={() => setCreditPackage(pkg.credits)}
                    >
                      {/* Credit Display Pill */}
                      <div className="flex justify-start mb-6">
                        <div className="bg-white px-3 py-1 rounded-full flex items-center shadow-sm">
                          <span className="text-black font-black text-xs">{pkg.credits}</span>
                        </div>
                      </div>

                      {/* Pricing Section */}
                      <div className="mb-6">
                        <div className="flex items-center gap-3">
                          <h2 className="text-white text-3xl font-black tracking-tighter mb-0">₹{pkg.price}</h2>
                          <div className="flex flex-col">
                            {pkg.original_price > pkg.price && (
                              <span className="text-[12px] text-[#64748b] line-through font-bold leading-none">₹{pkg.original_price}</span>
                            )}
                          </div>
                        </div>
                        <p className="text-[#64748b] text-[9px] font-bold uppercase tracking-widest mt-2">
                          {pkg.original_price > pkg.price ? `${Math.round(((pkg.original_price - pkg.price) / pkg.original_price) * 100)}% discount` : 'Special Pricing'}
                        </p>
                      </div>

                      <div className="mt-auto pt-5 border-t border-white/10">
                        <button
                          type="button"
                          className="w-full py-3.5 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] transition-all transform hover:scale-[1.02] active:scale-[0.98]"
                          style={{
                            backgroundColor: creditPackage === pkg.credits ? '#ef4444' : '#ffffff',
                            color: creditPackage === pkg.credits ? '#ffffff' : '#000000'
                          }}
                        >
                          {creditPackage === pkg.credits ? 'Selected Plan' : 'Buy now'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Regular Plans Section */}
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#64748b' }}></div>
                <h6 className="text-white font-bold uppercase tracking-widest text-[10px] mb-0">Choose your package</h6>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {creditPackages.map((pkg) => (
                  <div
                    key={pkg.credits}
                    className="rounded-2xl p-7 cursor-pointer transition-all relative overflow-hidden flex flex-col group border-2"
                    style={{
                      backgroundColor: '#0f1420',
                      borderColor: creditPackage === pkg.credits ? '#ef4444' : 'rgba(255, 255, 255, 0.05)',
                      boxShadow: creditPackage === pkg.credits ? '0 20px 40px rgba(239, 68, 68, 0.15)' : 'none'
                    }}
                    onClick={() => setCreditPackage(pkg.credits)}
                  >
                    {/* People Choice Badge */}
                    {pkg.popular && (
                      <div className="absolute top-6 right-8">
                        <span className="bg-[#22c55e] text-white px-3 py-1 rounded-lg text-[8px] font-black tracking-tight" style={{ borderBottomLeftRadius: '2px' }}>
                          People Choice
                        </span>
                      </div>
                    )}

                    {/* Credit Display Pill */}
                    <div className="flex justify-start mb-6">
                      <div className="bg-white px-3 py-1.5 rounded-full flex items-center gap-2 shadow-sm">
                        <i className="bi bi-database-fill text-dark text-sm"></i>
                        <span className="text-black font-black text-xs">{pkg.credits}</span>
                      </div>
                    </div>

                    {/* Pricing Section */}
                    <div className="mb-6">
                      <div className="flex items-center gap-3">
                        <h2 className="text-white text-3xl font-black tracking-tighter mb-0">₹{pkg.price}</h2>
                        <div className="flex flex-col">
                          {pkg.original_price > pkg.price && (
                            <span className="text-[12px] text-[#64748b] line-through font-bold leading-none">₹{pkg.original_price}</span>
                          )}
                        </div>
                      </div>
                      <p className="text-[#64748b] text-[9px] font-bold uppercase tracking-widest mt-2">
                        {pkg.original_price > pkg.price ? `${Math.round(((pkg.original_price - pkg.price) / pkg.original_price) * 100)}% discount` : 'Best Value Plan'}
                      </p>
                    </div>

                    <div className="mt-auto pt-5 border-t border-white/10">
                      <button
                        type="button"
                        className="w-full py-3.5 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] transition-all transform hover:scale-[1.02] active:scale-[0.98]"
                        style={{
                          backgroundColor: creditPackage === pkg.credits ? '#ef4444' : '#ffffff',
                          color: creditPackage === pkg.credits ? '#ffffff' : '#000000'
                        }}
                      >
                        {creditPackage === pkg.credits ? 'Selected Plan' : 'Buy now'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Purchase CTA */}
            <div className="mt-12 text-center">
              <button
                className="px-12 py-5 rounded-2xl text-sm font-bold uppercase tracking-widest transition-all inline-flex items-center gap-3 shadow-xl hover:scale-105"
                onClick={handlePurchaseCredits}
                disabled={purchasing}
                style={{
                  backgroundColor: '#ef4444',
                  color: '#fff',
                  boxShadow: '0 10px 30px rgba(239, 68, 68, 0.3)'
                }}
              >
                {purchasing ? (
                  <>
                    <span className="spinner-border spinner-border-sm"></span>
                    Processing...
                  </>
                ) : (
                  <>
                    Purchase {creditPackage} Credits
                    <i className="bi bi-arrow-right"></i>
                  </>
                )}
              </button>
              <div className="mt-4 flex items-center justify-center gap-3">
                <i className="bi bi-shield-check text-success"></i>
                <span className="text-[10px] uppercase font-bold tracking-widest" style={{ color: '#64748b' }}>Secure Payment via Razorpay</span>
              </div>
            </div>
          </div>

          {/* Enhanced Transaction History */}
          <div className="rounded-2xl overflow-hidden mb-8" style={{ backgroundColor: '#161e35' }}>
            <div className="p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
              <div className="flex items-center gap-3">
                <i className="bi bi-clock-history text-xl" style={{ color: '#ef4444' }}></i>
                <h5 className="text-lg font-bold text-white mb-0">Transaction History</h5>
                <span className="px-3 py-1 rounded-full text-[10px] font-bold" style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', color: '#64748b' }}>
                  {filteredTransactions.length} Total
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  className="px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all"
                  style={{ backgroundColor: '#0f1420', color: '#64748b' }}
                  onClick={exportToCSV}
                  disabled={filteredTransactions.length === 0}
                >
                  <i className="bi bi-file-earmark-excel me-1"></i>
                  Export CSV
                </button>
                <button
                  className="px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all"
                  style={{ backgroundColor: '#0f1420', color: '#64748b' }}
                  onClick={downloadReport}
                  disabled={filteredTransactions.length === 0}
                >
                  <i className="bi bi-file-earmark-pdf me-1"></i>
                  PDF Report
                </button>
                <button
                  className="px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all"
                  style={{ backgroundColor: showUsageHistory ? 'rgba(239, 68, 68, 0.1)' : '#0f1420', color: showUsageHistory ? '#ef4444' : '#64748b' }}
                  onClick={() => {
                    setShowUsageHistory(!showUsageHistory);
                    if (!showUsageHistory) fetchUsageHistory();
                  }}
                >
                  <i className="bi bi-graph-up me-1"></i>
                  Usage History
                </button>
              </div>
            </div>

            {/* Search and Filter Controls */}
            <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
              <div>
                <label className="text-[10px] uppercase font-bold tracking-widest mb-2 block" style={{ color: '#64748b' }}>Search</label>
                <div className="relative">
                  <i className="bi bi-search absolute left-4 top-1/2 -translate-y-1/2 text-[10px]" style={{ color: '#64748b' }}></i>
                  <input
                    type="text"
                    className="w-full pl-10 pr-4 py-3 rounded-xl text-xs outline-none transition-all"
                    style={{ backgroundColor: '#0f1420', color: '#fff', border: '1px solid rgba(255, 255, 255, 0.05)' }}
                    placeholder="Search transactions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              
              <div>
                <label className="text-[10px] uppercase font-bold tracking-widest mb-2 block" style={{ color: '#64748b' }}>Status</label>
                <select
                  className="w-full px-4 py-3 rounded-xl text-xs outline-none appearance-none cursor-pointer"
                  style={{ backgroundColor: '#0f1420', color: '#fff', border: '1px solid rgba(255, 255, 255, 0.05)' }}
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="completed" className="bg-[#0f1420]">Completed</option>
                  <option value="pending" className="bg-[#0f1420]">Pending</option>
                  <option value="failed" className="bg-[#0f1420]">Failed</option>
                </select>
              </div>

              <div>
                <label className="text-[10px] uppercase font-bold tracking-widest mb-2 block" style={{ color: '#64748b' }}>Type</label>
                <select
                  className="w-full px-4 py-3 rounded-xl text-xs outline-none appearance-none cursor-pointer"
                  style={{ backgroundColor: '#0f1420', color: '#fff', border: '1px solid rgba(255, 255, 255, 0.05)' }}
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="all">All Types</option>
                  <option value="purchase" className="bg-[#0f1420]">Purchase</option>
                  <option value="usage" className="bg-[#0f1420]">Usage</option>
                </select>
              </div>

              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="text-[10px] uppercase font-bold tracking-widest mb-2 block" style={{ color: '#64748b' }}>Date Range</label>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      className="w-full px-2 py-3 rounded-xl text-[10px] outline-none transition-all"
                      style={{ backgroundColor: '#0f1420', color: '#fff', border: '1px solid rgba(255, 255, 255, 0.05)' }}
                      value={dateRange.start}
                      onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                    />
                    <input
                      type="date"
                      className="w-full px-2 py-3 rounded-xl text-[10px] outline-none transition-all"
                      style={{ backgroundColor: '#0f1420', color: '#fff', border: '1px solid rgba(255, 255, 255, 0.05)' }}
                      value={dateRange.end}
                      onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                    />
                  </div>
                </div>
                <button
                  className="p-3 rounded-xl transition-all"
                  style={{ backgroundColor: '#0f1420', color: '#64748b', border: '1px solid rgba(255, 255, 255, 0.05)' }}
                  onClick={clearFilters}
                  title="Clear Filters"
                >
                  <i className="bi bi-x-circle text-sm"></i>
                </button>
              </div>
            </div>

            <div className="p-0">
              {filteredTransactions.length === 0 ? (
                <div className="text-center py-20">
                  <div className="mb-6 opacity-20">
                    <i className="bi bi-inbox" style={{ fontSize: '80px', color: '#64748b' }}></i>
                  </div>
                  <h5 className="font-bold text-white mb-2">
                    {transactions.length === 0 ? 'No transactions yet' : 'No matches found'}
                  </h5>
                  <p className="text-sm max-w-xs mx-auto mb-8" style={{ color: '#64748b' }}>
                    {transactions.length === 0
                      ? 'Your credit purchase and usage history will appear here once you start streaming.'
                      : 'Try adjusting your search criteria or clearing filters'
                    }
                  </p>
                  {transactions.length === 0 && (
                    <div className="max-w-md mx-auto p-6 rounded-2xl" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                       <h6 className="text-white font-bold mb-4 flex items-center justify-center gap-2">
                        <i className="bi bi-info-circle" style={{ color: '#ef4444' }}></i>
                        Getting Started
                      </h6>
                      <ul className="text-left text-xs space-y-3" style={{ color: '#94a3b8' }}>
                        <li className="flex items-center gap-2"><i className="bi bi-check2 text-success"></i> Purchase transactions with payment details</li>
                        <li className="flex items-center gap-2"><i className="bi bi-check2 text-success"></i> Credit usage during livestreams</li>
                        <li className="flex items-center gap-2"><i className="bi bi-check2 text-success"></i> Transaction status and timestamps</li>
                        <li className="flex items-center gap-2"><i className="bi bi-check2 text-success"></i> Downloadable invoices and receipts</li>
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                        <th className="px-8 py-5 text-left text-[10px] font-bold uppercase tracking-widest" style={{ color: '#64748b' }}>Date & Time</th>
                        <th className="px-8 py-5 text-left text-[10px] font-bold uppercase tracking-widest" style={{ color: '#64748b' }}>Activity</th>
                        <th className="px-8 py-5 text-left text-[10px] font-bold uppercase tracking-widest" style={{ color: '#64748b' }}>Credits</th>
                        <th className="px-8 py-5 text-left text-[10px] font-bold uppercase tracking-widest" style={{ color: '#64748b' }}>Amount</th>
                        <th className="px-8 py-5 text-left text-[10px] font-bold uppercase tracking-widest" style={{ color: '#64748b' }}>Status</th>
                        <th className="px-8 py-5 text-left text-[10px] font-bold uppercase tracking-widest" style={{ color: '#64748b' }}>Details</th>
                        <th className="px-8 py-5 text-right text-[10px] font-bold uppercase tracking-widest" style={{ color: '#64748b' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTransactions.map((transaction) => (
                        <tr key={transaction.id} className="transition-all hover:bg-white/[0.02]" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.02)' }}>
                          <td className="px-8 py-6">
                            <div className="text-white font-medium text-xs mb-1">{formatDate(transaction.created_at).split(',')[0]}</div>
                            <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#64748b' }}>{formatDate(transaction.created_at).split(',')[1]}</div>
                          </td>
                          <td className="px-8 py-6">
                            <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest" 
                              style={{ 
                                backgroundColor: transaction.transaction_type === 'purchase' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(239, 68, 68, 0.1)', 
                                color: transaction.transaction_type === 'purchase' ? '#3b82f6' : '#ef4444' 
                              }}>
                              <i className={`bi ${transaction.transaction_type === 'purchase' ? 'bi-plus-circle' : 'bi-dash-circle'} mr-2`}></i>
                              {transaction.transaction_type}
                            </span>
                          </td>
                          <td className="px-8 py-6">
                            <div className={`text-sm font-bold ${transaction.transaction_type === 'purchase' ? 'text-success' : 'text-danger'}`}>
                              {transaction.transaction_type === 'purchase' ? '+' : '-'}{transaction.credits}
                            </div>
                          </td>
                          <td className="px-8 py-6">
                            {transaction.amount ? (
                              <div className="text-white font-bold text-sm">₹{transaction.amount}</div>
                            ) : (
                              <div className="text-xs" style={{ color: '#64748b' }}>-</div>
                            )}
                          </td>
                          <td className="px-8 py-6">
                            <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest" 
                              style={{ 
                                backgroundColor: transaction.status === 'completed' ? 'rgba(16, 185, 129, 0.1)' : transaction.status === 'pending' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                color: transaction.status === 'completed' ? '#10b981' : transaction.status === 'pending' ? '#f59e0b' : '#ef4444'
                              }}>
                              {transaction.status}
                            </span>
                          </td>
                          <td className="px-8 py-6">
                            <div className="text-xs text-white truncate max-w-[200px] mb-1">{transaction.description}</div>
                            {transaction.razorpay_payment_id && (
                              <div className="text-[10px] font-mono opacity-30" style={{ color: '#fff' }}>#{transaction.razorpay_payment_id}</div>
                            )}
                          </td>
                          <td className="px-8 py-6 text-right">
                            <div className="flex justify-end gap-2">
                              {transaction.transaction_type === 'purchase' && transaction.status === 'completed' && (
                                <button
                                  className="w-8 h-8 rounded-lg flex items-center justify-center transition-all bg-white/[0.05] hover:bg-white/[0.1] text-white"
                                  onClick={() => downloadInvoice(transaction)}
                                  title="Download Invoice"
                                >
                                  <i className="bi bi-download text-xs"></i>
                                </button>
                              )}
                              <button className="w-8 h-8 rounded-lg flex items-center justify-center transition-all bg-white/[0.05] hover:bg-white/[0.1]" style={{ color: '#64748b' }}>
                                <i className="bi bi-three-dots-vertical text-xs"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination Replacement */}
                  <div className="p-6 flex items-center justify-between" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#64748b' }}>
                      Showing {filteredTransactions.length} of {transactions.length} records
                    </p>
                    <div className="flex gap-2">
                        <button className="w-8 h-8 rounded-lg flex items-center justify-center transition-all bg-white/[0.02] border border-white/[0.05]" style={{ color: '#64748b' }}>
                            <i className="bi bi-chevron-left text-xs"></i>
                        </button>
                        <button className="w-8 h-8 rounded-lg flex items-center justify-center transition-all bg-white/[0.02] border border-white/[0.05]" style={{ color: '#64748b' }}>
                            <i className="bi bi-chevron-right text-xs"></i>
                        </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Usage History Modal */}
          {showUsageHistory && (
            <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(5, 5, 17, 0.9)' }}>
              <div className="w-full max-w-4xl rounded-3xl overflow-hidden shadow-2xl" style={{ backgroundColor: '#161e35', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                <div className="p-8 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                  <div className="flex items-center gap-3">
                    <i className="bi bi-graph-up text-xl" style={{ color: '#ef4444' }}></i>
                    <h5 className="text-lg font-bold text-white mb-0">Usage History & Analytics</h5>
                  </div>
                  <button
                    type="button"
                    className="w-10 h-10 rounded-xl flex items-center justify-center transition-all bg-white/[0.05] hover:bg-white/[0.1] text-white"
                    onClick={() => setShowUsageHistory(false)}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
                <div className="p-10">
                  {loadingUsage ? (
                    <div className="text-center py-20">
                      <div className="spinner-border" style={{ color: '#ef4444' }} role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-4 text-xs font-bold uppercase tracking-widest" style={{ color: '#64748b' }}>Analyzing patterns...</p>
                    </div>
                  ) : (
                    <div className="text-center py-20">
                       <i className="bi bi-bar-chart-line opacity-20 mb-6 block" style={{ fontSize: '60px', color: '#64748b' }}></i>
                        <p className="text-white font-medium mb-2">Detailed usage analytics coming soon</p>
                        <p className="text-sm max-w-md mx-auto" style={{ color: '#64748b' }}>We're building a more comprehensive view of your livestream performance and credit consumption patterns.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
};

export default CreditsManagement;




